import { useState } from "react";
/* eslint-disable react-refresh/only-export-components */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { friendlyError } from "@/lib/errors";
import { getActiveCurrencyCode } from "@/lib/currency";

/**
 * Supplier linking during product creation — "enter once, reuse everywhere".
 *
 * Lets an operator pick a supplier (or add a new one inline) and capture the
 * supplier SKU, minimum order quantity, order multiple, lead time and a
 * preferred flag. It only holds a *selection*; the parent persists the actual
 * supplier–product relationship through the canonical `supplier-products`
 * service after the product is created, so the relationship has exactly one
 * write path regardless of which side of the relationship it was entered from.
 */

export interface SupplierSelection {
  supplierId: string;
  supplierSku: string;
  minOrderQty: string;
  orderMultiple: string;
  leadTimeDays: string;
  isPreferred: boolean;
}

export const emptySupplierSelection: SupplierSelection = {
  supplierId: "",
  supplierSku: "",
  minOrderQty: "",
  orderMultiple: "",
  leadTimeDays: "",
  isPreferred: false,
};

const inputCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";

interface Props {
  orgId: string;
  value: SupplierSelection;
  costPrice: string;
  onChange: (value: SupplierSelection) => void;
}

export default function SupplierSection({ orgId, value, costPrice, onChange }: Props) {
  const queryClient = useQueryClient();
  const [quickAdd, setQuickAdd] = useState(false);
  const [quickName, setQuickName] = useState("");
  const [quickEmail, setQuickEmail] = useState("");
  const [quickPhone, setQuickPhone] = useState("");
  const [quickError, setQuickError] = useState("");
  const [saving, setSaving] = useState(false);

  const { data: suppliers } = useQuery({
    queryKey: ["suppliers-for-product", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await supabase
        .from("suppliers")
        .select("id, name")
        .eq("org_id", orgId)
        .eq("status", "active")
        .order("name");
      return (data ?? []) as { id: string; name: string }[];
    },
  });

  const set = (key: keyof SupplierSelection, v: string | boolean) =>
    onChange({ ...value, [key]: v });

  const createSupplier = async () => {
    setQuickError("");
    if (!quickName.trim()) {
      setQuickError("Enter a supplier name.");
      return;
    }
    setSaving(true);
    const { data, error } = await supabase
      .from("suppliers")
      .insert({
        org_id: orgId,
        name: quickName.trim(),
        email: quickEmail.trim() || null,
        phone: quickPhone.trim() || null,
        currency: getActiveCurrencyCode(),
      })
      .select("id")
      .maybeSingle();
    setSaving(false);
    if (error) {
      setQuickError(friendlyError(error.message));
      return;
    }
    if (data?.id) {
      onChange({ ...value, supplierId: data.id as string });
      queryClient.invalidateQueries({ queryKey: ["suppliers-for-product", orgId] });
      setQuickAdd(false);
      setQuickName("");
      setQuickEmail("");
      setQuickPhone("");
    }
  };

  const noSuppliers = (suppliers ?? []).length === 0;

  return (
    <div className="space-y-3">
      <div>
        <label className={labelCls}>Supplier</label>
        {noSuppliers && !quickAdd ? (
          <div className="px-3 py-2.5 bg-amber-50 border border-amber-100 rounded-lg text-xs text-amber-700">
            <i className="ri-truck-line mr-1" />
            No suppliers yet. Add one now to link this product to where you buy it.
          </div>
        ) : (
          <select
            value={value.supplierId}
            onChange={(e) => set("supplierId", e.target.value)}
            className={inputCls}
          >
            <option value="">No supplier</option>
            {(suppliers ?? []).map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        )}
        <button
          type="button"
          onClick={() => setQuickAdd((v) => !v)}
          className="mt-1 text-xs font-medium text-emerald-600 hover:text-emerald-700 flex items-center gap-1 whitespace-nowrap"
        >
          <i className="ri-add-line" /> {quickAdd ? "Cancel adding supplier" : "Add a new supplier"}
        </button>
      </div>

      {quickAdd && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 space-y-2">
          {quickError && <p className="text-xs text-red-600">{quickError}</p>}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <label className={labelCls}>Name *</label>
              <input
                value={quickName}
                onChange={(e) => setQuickName(e.target.value)}
                className={inputCls}
                placeholder="ABC Clothing"
              />
            </div>
            <div>
              <label className={labelCls}>Email</label>
              <input
                type="email"
                value={quickEmail}
                onChange={(e) => setQuickEmail(e.target.value)}
                className={inputCls}
                placeholder="orders@abc.com"
              />
            </div>
            <div>
              <label className={labelCls}>Phone</label>
              <input
                value={quickPhone}
                onChange={(e) => setQuickPhone(e.target.value)}
                className={inputCls}
                placeholder="+61 2 0000 0000"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={createSupplier}
            disabled={saving}
            className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-xs font-medium rounded-lg whitespace-nowrap"
          >
            {saving ? "Adding..." : "Add supplier"}
          </button>
        </div>
      )}

      {value.supplierId && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Supplier SKU</label>
            <input
              value={value.supplierSku}
              onChange={(e) => set("supplierSku", e.target.value)}
              className={inputCls}
              placeholder="ABC-TS001"
            />
            <p className="text-xs text-slate-400 mt-1">The code your supplier uses for this product.</p>
          </div>
          <div>
            <label className={labelCls}>Minimum order quantity</label>
            <input
              type="number"
              min="1"
              value={value.minOrderQty}
              onChange={(e) => set("minOrderQty", e.target.value)}
              className={inputCls}
              placeholder="1"
            />
          </div>
          <div>
            <label className={labelCls}>Order multiple</label>
            <input
              type="number"
              min="1"
              value={value.orderMultiple}
              onChange={(e) => set("orderMultiple", e.target.value)}
              className={inputCls}
              placeholder="1"
            />
            <p className="text-xs text-slate-400 mt-1">Order in multiples of this many.</p>
          </div>
          <div>
            <label className={labelCls}>Lead time (days)</label>
            <input
              type="number"
              min="0"
              value={value.leadTimeDays}
              onChange={(e) => set("leadTimeDays", e.target.value)}
              className={inputCls}
              placeholder="14"
            />
          </div>
        </div>
      )}

      {value.supplierId && (
        <>
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={value.isPreferred}
              onChange={(e) => set("isPreferred", e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
            />
            Preferred supplier for this product
          </label>
          <p className="text-xs text-slate-400">
            <i className="ri-information-line mr-1" />
            Supplier price is set from the cost you entered above
            {costPrice ? ` ($${Number(costPrice).toFixed(2)})` : ""}. You can adjust it on the product page later.
          </p>
        </>
      )}
    </div>
  );
}
